import React, { useEffect, useRef, useState } from 'react'
import GooglePlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete'
import AsyncSelect from 'react-select/async'
import { get } from 'helpers/api_helper'
import { withFloatingSurface } from 'components/Common/selectSurface'

// The API resolves the canonical county from `administrativeArea` and `countryCode`.
export const normalizePlayerLocation = (result, coords, country) => {
  const component = type => result?.address_components?.find(item => item.types?.includes(type))
  const countryCode = component('country')?.short_name?.toUpperCase()
  if (countryCode !== country?.key?.toUpperCase()) throw new Error('Choose a city in the selected country.')
  const city = component('locality')?.long_name || component('postal_town')?.long_name || component('administrative_area_level_2')?.long_name
  if (!city || !Number.isFinite(coords?.lat) || !Number.isFinite(coords?.lng)) throw new Error('This city could not be verified.')
  const area = component('administrative_area_level_1')
  return { city, country: country.name, name: result.formatted_address || city, coords, countryCode,
    ...(result.place_id ? { placeId: result.place_id } : {}),
    ...(area ? { administrativeArea: { shortName: area.short_name, longName: area.long_name } } : {}) }
}
export function PlayerCityPicker({ name, label, country, value, onChange, onBusy, locked, error }) {
  const [failure, setFailure] = useState('')
  const [loading, setLoading] = useState(false)
  const [retry, setRetry] = useState(0)
  const [selected, setSelected] = useState(null)
  const generation = useRef(0)
  useEffect(() => {
    generation.current += 1
    setFailure(''); setSelected(null); setLoading(false)
    onBusy(current => ({ ...current, [name]: false }))
    return () => { generation.current += 1 }
  }, [country?.key, name, onBusy])
  const choose = async option => {
    const request = ++generation.current
    setSelected(option); setFailure(''); onChange(null)
    if (!option) {
      setLoading(false); onBusy(current => ({ ...current, [name]: false })); return
    }
    setLoading(true); onBusy(current => ({ ...current, [name]: true }))
    try {
      const [result] = await geocodeByPlaceId(option.value.place_id)
      const coords = await getLatLng(result)
      const location = normalizePlayerLocation(result, coords, country)
      if (request === generation.current) onChange(location)
    } catch (failure) {
      if (request === generation.current) setFailure(failure.message || 'Could not verify this city. Try again.')
    } finally {
      if (request === generation.current) {
        setLoading(false); onBusy(current => ({ ...current, [name]: false }))
      }
    }
  }
  return <div className="form-group mb-0">
    <label htmlFor={'player-' + name}>{label}<span aria-hidden="true"> *</span></label>
    {country ? <GooglePlacesAutocomplete key={country.key + ':' + retry} apiKey={process.env.REACT_APP_GOOGLE_MAPS_API}
      apiOptions={{ language: 'en' }} minLengthAutocomplete={2}
      onLoadFailed={() => setFailure('Could not load city search. Try again.')}
      autocompletionRequest={{ types: ['(cities)'], componentRestrictions: { country: country.key.toLowerCase() } }}
      selectProps={{ styles: withFloatingSurface(),  classNamePrefix: 'player-select', inputId: 'player-' + name, name, 'aria-label': label, 'aria-required': true,
        'aria-invalid': Boolean(error || failure), 'aria-describedby': 'player-' + name + '-help',
        value: value ? { label: value.name, value: value } : selected,
        onChange: choose, isClearable: true, isDisabled: locked, isLoading: loading,
        placeholder: 'Search for a city…', noOptionsMessage: () => 'Type a city name',
      }} /> : <input className="form-control" id={'player-' + name} disabled placeholder="Select a country first" />}
    <small id={'player-' + name + '-help'} className={failure || error ? 'text-danger' : 'text-muted'}>
      {loading ? 'Verifying city…' : failure || error || 'Choose a city from the search results.'}
    </small>
    {failure && <button type="button" className="btn btn-link p-0" disabled={locked} onClick={() => selected ? choose(selected) : setRetry(value => value + 1)}>Retry city search</button>}
  </div>
}
export function OrganizerClubPicker({ value, onChange, onBusy, locked }) {
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  const [labels, setLabels] = useState({})
  const [labelError, setLabelError] = useState(false)
  const unresolvedIds = value.filter(option => option.label === option.value).map(option => option.value).join(',')
  useEffect(() => {
    let active = true
    setLabelError(false)
    onBusy(current => ({ ...current, clubLabels: Boolean(unresolvedIds) }))
    if (unresolvedIds) Promise.allSettled(unresolvedIds.split(',').map(async id => {
      const response = await get('/clubs/' + id), club = response?.data
      if (String(club?.id || club?._id) !== id || !club?.clubName) throw new Error('Club unavailable')
      return [id, club.clubName + (club.location?.city ? ' · ' + club.location.city : '') + (club.status !== 'activated' ? ' (inactive)' : '')]
    })).then(results => {
      if (!active) return
      setLabels(current => ({ ...current, ...Object.fromEntries(results.filter(result => result.status === 'fulfilled').map(result => result.value)) }))
      setLabelError(results.some(result => result.status === 'rejected'))
      onBusy(current => ({ ...current, clubLabels: false }))
    })
    return () => { active = false }
  }, [unresolvedIds, retry, onBusy])
  const generation = useRef(0)
  useEffect(() => () => { generation.current += 1 }, [])
  const load = async search => {
    const request = ++generation.current
    setError(''); onBusy(current => ({ ...current, clubs: true }))
    try {
      const response = await get('/clubs?' + new URLSearchParams({ status: 'activated', search, limit: 25, page: 1 }))
      if (!Array.isArray(response?.data?.results)) throw new Error('Invalid response')
      return response.data.results.filter(club => club.status === 'activated').map(club => ({ value: club.id || club._id,
        label: club.clubName + (club.location?.city ? ' · ' + club.location.city : '') }))
    } catch {
      if (request === generation.current) setError('Could not load clubs. Retry or search again.')
      return []
    } finally {
      if (request === generation.current) onBusy(current => ({ ...current, clubs: false }))
    }
  }
  return <div>
    <label htmlFor="player-organizerClubs">Assigned clubs (optional)</label>
    <AsyncSelect styles={withFloatingSurface()} classNamePrefix="player-select" key={retry} inputId="player-organizerClubs" isMulti isClearable isDisabled={locked}
      defaultOptions loadOptions={load} value={value.map(option => ({ ...option, label: labels[option.value] || option.label }))} onChange={options => onChange(options || [])} />
    {labelError && <small className="text-danger">Some assigned club names could not be loaded. <button type="button" className="btn btn-link p-0" disabled={locked} onClick={() => setRetry(value => value + 1)}>Retry assigned clubs</button></small>}
    {error && <small className="text-danger">{error} <button type="button" className="btn btn-link p-0" onClick={() => setRetry(value => value + 1)}>Retry clubs</button></small>}
  </div>
}
